import axios, { AxiosInstance } from 'axios'
import toast from 'react-hot-toast'
import {
  AdminUser,
  LoginForm,
  User,
  Generation,
  MLSProvider,
  MLSExportBatch,
  Property,
  SystemHealth,
  DashboardStats,
  RevenueMetrics,
  WorkerStatus,
  AuditLog,
  ApiResponse,
  PaginatedResponse,
  UserFilters,
  GenerationFilters,
  MLSFilters,
  UserUpdateForm,
  CreditAdjustmentForm
} from '../types'

class AdminApiClient {
  private client: AxiosInstance
  private workflowClient: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: (import.meta as any).env?.VITE_API_BASE_URL || '/api/admin',
      timeout: parseInt((import.meta as any).env?.VITE_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    // Create separate client for workflow APIs
    this.workflowClient = axios.create({
      baseURL: (import.meta as any).env?.VITE_API_BASE_URL || '/api/admin',
      timeout: parseInt((import.meta as any).env?.VITE_API_TIMEOUT || '30000'),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('admin_token')
          window.location.href = '/login'
        } else if (error.response?.status >= 500) {
          toast.error('Server error occurred. Please try again.')
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to perform this action.')
        }
        return Promise.reject(error)
      }
    )

    // Setup interceptors for workflow client
    this.workflowClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    this.workflowClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token')
          window.location.href = '/login'
        } else if (error.response?.status >= 500) {
          toast.error('Server error occurred. Please try again.')
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to perform this action.')
        }
        return Promise.reject(error)
      }
    )
  }

  setAuthToken(token: string | null) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
      this.workflowClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete this.client.defaults.headers.common['Authorization']
      delete this.workflowClient.defaults.headers.common['Authorization']
    }
  }

  // Authentication
  async login(credentials: LoginForm): Promise<ApiResponse<{ token: string; user: AdminUser }>> {
    const response = await this.client.post('/auth/login', credentials)
    return response.data
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout')
  }

  async refreshToken(): Promise<ApiResponse<{ token: string; user: AdminUser }>> {
    const response = await this.client.post('/auth/refresh')
    return response.data
  }

  // Dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.client.get('/dashboard/stats')
    return response.data
  }

  async getSystemHealth(): Promise<ApiResponse<SystemHealth>> {
    const response = await this.client.get('/system/health')
    return response.data
  }

  // User Management
  async getUsers(
    page: number = 1,
    perPage: number = 20,
    filters?: UserFilters
  ): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('per_page', perPage.toString())
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })
    }
    
    const response = await this.client.get(`/users?${params}`)
    return response.data
  }

  async getUser(userId: string): Promise<ApiResponse<User>> {
    const response = await this.client.get(`/users/${userId}`)
    return response.data
  }

  async updateUser(userId: string, data: UserUpdateForm): Promise<ApiResponse<User>> {
    const response = await this.client.put(`/users/${userId}`, data)
    return response.data
  }

  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/users/${userId}`)
    return response.data
  }

  async adjustUserCredits(userId: string, data: CreditAdjustmentForm): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/users/${userId}/credits`, data)
    return response.data
  }

  async getUserGenerations(userId: string): Promise<ApiResponse<Generation[]>> {
    const response = await this.client.get(`/users/${userId}/generations`)
    return response.data
  }

  async getUserProperties(userId: string): Promise<ApiResponse<Property[]>> {
    const response = await this.client.get(`/users/${userId}/properties`)
    return response.data
  }

  async exportUserData(userId: string): Promise<Blob> {
    const response = await this.client.get(`/users/${userId}/export`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Generation Management
  async getGenerations(
    page: number = 1,
    perPage: number = 20,
    filters?: GenerationFilters
  ): Promise<PaginatedResponse<Generation>> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('per_page', perPage.toString())
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })
    }
    
    const response = await this.client.get(`/generations?${params}`)
    return response.data
  }

  async getGeneration(generationId: string): Promise<ApiResponse<Generation>> {
    const response = await this.client.get(`/generations/${generationId}`)
    return response.data
  }

  async retryGeneration(generationId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/generations/${generationId}/retry`)
    return response.data
  }

  async cancelGeneration(generationId: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/generations/${generationId}`)
    return response.data
  }

  async getGenerationStats(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/generations/stats')
    return response.data
  }

  // MLS Management
  async getMLSProviders(): Promise<ApiResponse<MLSProvider[]>> {
    const response = await this.client.get('/mls/providers')
    return response.data
  }

  async getMLSProvider(providerId: string): Promise<ApiResponse<MLSProvider>> {
    const response = await this.client.get(`/mls/providers/${providerId}`)
    return response.data
  }

  async updateMLSProvider(providerId: string, data: Partial<MLSProvider>): Promise<ApiResponse<MLSProvider>> {
    const response = await this.client.put(`/mls/providers/${providerId}`, data)
    return response.data
  }

  async getMLSExportBatches(
    page: number = 1,
    perPage: number = 20,
    filters?: MLSFilters
  ): Promise<PaginatedResponse<MLSExportBatch>> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    })
    const response = await this.client.get(`/mls/export-batches?${params}`)
    return response.data
  }

  async getMLSExportBatch(batchId: string): Promise<ApiResponse<MLSExportBatch>> {
    const response = await this.client.get(`/mls/export-batches/${batchId}`)
    return response.data
  }

  async retryMLSExportBatch(batchId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/mls/export-batches/${batchId}/retry`)
    return response.data
  }

  async cancelMLSExportBatch(batchId: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/mls/export-batches/${batchId}`)
    return response.data
  }

  // Financial Management
  async getRevenueMetrics(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<ApiResponse<RevenueMetrics>> {
    const response = await this.client.get(`/revenue-metrics?period=${period}`)
    return response.data
  }

  async getPaymentTransactions(
    page: number = 1,
    perPage: number = 20
  ): Promise<PaginatedResponse<any>> {
    const response = await this.client.get(`/payment-transactions?page=${page}&per_page=${perPage}`)
    return response.data
  }

  async getStripeMetrics(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/financial/stripe-metrics')
    return response.data
  }

  // Worker Monitoring
  async getWorkerStatus(): Promise<ApiResponse<WorkerStatus[]>> {
    const response = await this.client.get('/worker-status')
    return response.data
  }

  async getQueueStatus(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/workers/queues')
    return response.data
  }

  async restartWorkers(): Promise<ApiResponse<void>> {
    const response = await this.client.post('/workers/restart')
    return response.data
  }

  async purgeQueue(queueName: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/workers/queues/${queueName}/purge`)
    return response.data
  }

  // Analytics
  async getAnalyticsData(metric: string, period: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/analytics/${metric}?period=${period}`)
    return response.data
  }

  async getUserEngagementMetrics(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/analytics/user-engagement')
    return response.data
  }

  async getFeatureUsageMetrics(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/analytics/feature-usage')
    return response.data
  }

  // Audit Logs
  async getAuditLogs(
    page: number = 1,
    perPage: number = 50
  ): Promise<PaginatedResponse<AuditLog>> {
    const response = await this.client.get(`/audit-logs?page=${page}&per_page=${perPage}`)
    return response.data
  }

  async getAuditLog(logId: string): Promise<ApiResponse<AuditLog>> {
    const response = await this.client.get(`/audit/logs/${logId}`)
    return response.data
  }

  // System Configuration
  async getSystemConfig(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/system/config')
    return response.data
  }

  async updateSystemConfig(config: any): Promise<ApiResponse<any>> {
    const response = await this.client.put('/system/config', config)
    return response.data
  }

  async toggleMaintenanceMode(enabled: boolean): Promise<ApiResponse<void>> {
    const response = await this.client.post('/system/maintenance', { enabled })
    return response.data
  }

  // Workflow Management
  async getWorkflowsOverview(): Promise<ApiResponse<any>> {
    const response = await this.workflowClient.get('/workflows/overview')
    return response.data
  }

  async getWorkflowHistory(qualityTier: string): Promise<ApiResponse<any>> {
    const response = await this.workflowClient.get(`/workflows/quality/${qualityTier}/history`)
    return response.data
  }

  async getWorkflowDetails(workflowId: string): Promise<ApiResponse<any>> {
    const response = await this.workflowClient.get(`/workflows/workflow/${workflowId}`)
    return response.data
  }

  async uploadWorkflow(request: any): Promise<ApiResponse<any>> {
    const response = await this.workflowClient.post('/workflows/upload', request)
    return response.data
  }

  async activateWorkflow(workflowId: string): Promise<ApiResponse<void>> {
    const response = await this.workflowClient.post(`/workflows/activate/${workflowId}`)
    return response.data
  }

  async testWorkflow(workflowId: string, testImage?: File): Promise<ApiResponse<any>> {
    const formData = new FormData()
    if (testImage) {
      formData.append('test_image', testImage)
    }
    
    const response = await this.workflowClient.post(`/workflows/test/${workflowId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async deleteWorkflow(workflowId: string): Promise<ApiResponse<void>> {
    const response = await this.workflowClient.delete(`/workflows/workflow/${workflowId}`)
    return response.data
  }

  async rollbackWorkflow(qualityTier: string, targetVersion: number, reason: string): Promise<ApiResponse<void>> {
    const formData = new FormData()
    formData.append('quality_tier', qualityTier)
    formData.append('target_version', targetVersion.toString())
    formData.append('rollback_reason', reason)
    
    const response = await this.workflowClient.post('/workflows/rollback', formData)
    return response.data
  }

  async validateWorkflowJson(workflowJson: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.workflowClient.post('/workflows/validate', { workflow_json: workflowJson })
    return response.data
  }

  // Reports
  async generateReport(reportType: string, params: any): Promise<Blob> {
    const response = await this.client.post('/reports', { report_type: reportType, params }, {
      responseType: 'blob'
    })
    return response.data
  }

  async scheduleReport(reportConfig: any): Promise<ApiResponse<void>> {
    const response = await this.client.post('/reports/schedule', reportConfig)
    return response.data
  }
}

export const adminApi = new AdminApiClient()